import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useData } from '../contexts/DataContext';
import { useT, WORK_TYPES } from '../lib/i18n';
import { computeSummary } from '../lib/taxService';
import { validateSubmission } from '../lib/validationService';
import { PROVINCES } from '../lib/taxService';
import { generatePDF } from '../lib/pdf';
import { getCatalogFor, getUnitsFor } from '../lib/materialsCatalog';
import { ChevronLeft, ChevronRight, Plus, Trash2, Check, AlertCircle, BookOpen, Sparkles, X, Printer, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { supabase } from '../lib/supabaseClient';
import SupplierPicker from '../components/SupplierPicker';
import MetreCalculator from '../components/MetreCalculator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['client', 'work', 'materials', 'labour', 'summary'];

export default function NewSubmission() {
  const { id } = useParams();
  const nav = useNavigate();
  const { saveSubmission, profile, getSubmission, submissionFromTemplate, saveTemplate } = useData();
  const { t, lang } = useT();
  const [step, setStep] = useState(0);
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcMaterialIdx, setCalcMaterialIdx] = useState(null);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState('');
  const [form, setForm] = useState(() => ({
    client_name: '',
    client_address: '',
    client_phone: '',
    client_email: '',
    work_type: '',
    province: profile?.province || 'QC',
    notes: '',
    materials: [],
    labour: {
      uniform: true,
      hourly_rate: profile?.default_hourly_rate || 65,
      workers: 1,
      hours: 0,
      individuals: [],
    },
    margin: profile?.default_margin || 15,
    miscFees: 0,
    miscNotes: '',
  }));

  const loadedIdRef = useRef(null);
  useEffect(() => {
    if (!id) return;
    if (loadedIdRef.current === id) return; // Already loaded this submission; don't overwrite edits
    loadedIdRef.current = id;
    getSubmission(id).then((s) => {
      if (s) {
        setForm({
          id: s.id,
          submission_number: s.submission_number,
          client_name: s.client_name,
          client_address: s.client_address,
          client_phone: s.client_phone,
          client_email: s.client_email,
          work_type: s.work_type,
          province: s.province,
          notes: s.notes,
          materials: s.materials || [],
          labour: s.labour || { hourly_rate: 65, workers: 1, hours: 0 },
          margin: s.summary?.marginPct ?? 15,
          miscFees: s.summary?.miscFees ?? 0,
          miscNotes: s.summary?.miscNotes ?? s.miscNotes ?? '',
          created_at: s.created_at,
          expires_at: s.expires_at,
          status: s.status,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // === Load from a template if user navigated from /templates ===
  // We use sessionStorage as a one-shot signal (cleared immediately after use).
  const templateAppliedRef = useRef(false);
  useEffect(() => {
    if (id) return; // editing an existing submission, ignore templates
    if (templateAppliedRef.current) return;
    let tplId = null;
    try {
      tplId = window.sessionStorage.getItem('soumisspro_use_template_id');
    } catch { /* ignore */ }
    if (!tplId) return;
    templateAppliedRef.current = true;
    try { window.sessionStorage.removeItem('soumisspro_use_template_id'); } catch { /* ignore */ }
    submissionFromTemplate(tplId).then((draft) => {
      if (!draft) return;
      setForm((f) => ({ ...f, ...draft }));
      toast.success(lang === 'fr' ? '✓ Modèle appliqué' : '✓ Template applied');
      // Jump to the materials step so the user immediately sees what was loaded
      setStep(2);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const summary = useMemo(
    () => computeSummary(form.materials, form.labour, form.margin, form.miscFees, form.province, form.miscNotes, lang),
    [form.materials, form.labour, form.margin, form.miscFees, form.province, form.miscNotes, lang]
  );

  // === Draft auto-save (survives tab switches, refreshes, app backgrounding) ===
  // Persist the in-flight form to localStorage so the user never loses data
  // when they switch browser tabs (e.g. checking another site) or background
  // the PWA. Each submission id (or 'new') gets its own key.
  const draftKey = id ? `soumisspro_draft_${id}` : 'soumisspro_draft_new';
  const draftRestoredRef = useRef(false);

  // Restore on first mount (before any user interaction).
  // For NEW submissions only — for edits the supabase fetch above is authoritative
  // and we only restore the draft if the user had unsaved changes since opening.
  useEffect(() => {
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && (parsed.client_name || parsed.work_type || (parsed.materials && parsed.materials.length))) {
        // For edits, only restore if the draft has the same id as the URL param.
        if (id && parsed.id && parsed.id !== id) return;
        // Restore the full form + the wizard step so the user lands exactly
        // where they left off after switching tabs / backgrounding the app.
        const { __step, ...formData } = parsed;
        setForm((f) => ({ ...f, ...formData }));
        if (typeof __step === 'number' && __step >= 0 && __step < STEPS.length) {
          setStep(__step);
        }
      }
    } catch { /* ignore corrupted draft */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save on every change (synchronous; localStorage is fast enough for our payload size).
  // We also persist the current wizard step under __step so a tab-switch / reload
  // brings the user back to the exact step they were on, not step 0.
  useEffect(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify({ ...form, __step: step }));
    } catch { /* quota / private mode */ }
  }, [form, step, draftKey]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateLabour = (patch) => setForm((f) => ({ ...f, labour: { ...f.labour, ...patch } }));

  const addMaterial = () =>
    update({ materials: [...form.materials, { id: crypto.randomUUID(), description: '', quantity: 1, unit: 'un', unit_price: 0 }] });
  const updateMaterial = (i, patch) => {
    const arr = [...form.materials];
    arr[i] = { ...arr[i], ...patch };
    update({ materials: arr });
  };
  const removeMaterial = (i) => {
    const arr = [...form.materials];
    arr.splice(i, 1);
    update({ materials: arr });
  };

  const canNext = () => {
    if (step === 0) return form.client_name.trim() && form.client_address.trim() && form.client_phone.trim();
    if (step === 1) return !!form.work_type;
    if (step === 2 || step === 3) return true;
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const save = async (action = 'save') => {
    const payload = { ...form, summary };
    if (!form.id) payload.status = 'pending';
    const validation = validateSubmission(payload, t);
    if (!validation.valid) {
      toast.error(validation.errors[0]);
      return;
    }
    validation.warnings.forEach((w) => toast.warning(w));
    const saved = await saveSubmission(payload);
    toast.success('✓ ' + (lang === 'fr' ? 'Soumission enregistrée' : 'Submission saved'));
    // Clear both the "new submission" draft and any edit draft for this id
    // so we don't repopulate stale data on the next visit.
    try {
      localStorage.removeItem('soumisspro_draft_new');
      if (saved?.id) localStorage.removeItem(`soumisspro_draft_${saved.id}`);
      if (id) localStorage.removeItem(`soumisspro_draft_${id}`);
    } catch { /* ignore */ }
    if (action === 'pdf') {
      generatePDF(saved, profile, t, { lang });
    } else if (action === 'print') {
      generatePDF(saved, profile, t, { print: true, lang });
    }
    nav(`/submissions/${saved.id}`);
  };

  return (
    <Layout title={form.submission_number || t('dash.new_submission')}>
      {/* progress */}
      <div className="flex items-center gap-1.5 mb-8">
        {STEPS.map((s, i) => (
          <div
            key={s}
            data-testid={`wizard-step-indicator-${i}`}
            className={`flex-1 h-1.5 rounded-full transition-all ${i <= step ? 'bg-[#7A1F2B]' : 'bg-gray-200'}`}
          />
        ))}
      </div>
      <div className="mb-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">
          {t('wizard.step')} {step + 1} {t('wizard.of')} {STEPS.length}
        </div>
        <h1 className="font-heading text-3xl font-black tracking-tighter mt-1">
          {t(`wizard.${STEPS[step]}`)}
        </h1>
      </div>

      {step === 0 && (
        <div className="space-y-4">
          <Field label={t('client.name')} required>
            <input data-testid="client-name" className="field" value={form.client_name} onChange={(e) => update({ client_name: e.target.value })} />
          </Field>
          <Field label={t('client.address')} required>
            <input data-testid="client-address" className="field" value={form.client_address} onChange={(e) => update({ client_address: e.target.value })} />
          </Field>
          <Field label={t('client.phone')} required>
            <input data-testid="client-phone" type="tel" inputMode="tel" className="field" value={form.client_phone} onChange={(e) => update({ client_phone: e.target.value })} />
          </Field>
          <Field label={t('client.email')}>
            <input data-testid="client-email" type="email" className="field" value={form.client_email} onChange={(e) => update({ client_email: e.target.value })} />
          </Field>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <Field label={t('work.type')} required>
            <div className="grid grid-cols-2 gap-2">
              {WORK_TYPES[lang].map((w) => (
                <button
                  key={w}
                  data-testid={`work-type-${w}`}
                  onClick={() => update({ work_type: w })}
                  className={`min-h-[52px] px-3 rounded-xl border-2 text-left text-sm font-semibold transition-all ${
                    form.work_type === w ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white' : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
          </Field>
          <Field label={t('work.province')}>
            <select data-testid="work-province" className="field" value={form.province} onChange={(e) => update({ province: e.target.value })}>
              {PROVINCES.map((p) => {
                const label = `${p.code} — ${p.name}`;
                return <option key={p.code} value={p.code}>{label}</option>;
              })}
            </select>
          </Field>
          <Field label={t('work.notes')}>
            <textarea className="field min-h-[100px] py-3" value={form.notes} onChange={(e) => update({ notes: e.target.value })} />
          </Field>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <SupplierPicker
              lang={lang}
              onPick={(item) => {
                update({
                  materials: [
                    ...form.materials,
                    {
                      id: crypto.randomUUID(),
                      description: item.name,
                      quantity: 1,
                      unit: item.unit,
                      unit_price: item.price,
                      note: '',
                    },
                  ],
                });
              }}
            />
            <button
              type="button"
              data-testid="open-metre-calc-btn"
              onClick={() => { setCalcMaterialIdx(null); setCalcOpen(true); }}
              className="btn-outline gap-2 !min-h-[48px] text-sm relative"
              style={{ borderColor: '#C9A961', color: '#5A1620' }}
            >
              <Calculator size={16} />
              {lang === 'fr' ? 'Calculateur de métré' : 'Quantity calculator'}
              <span
                className="absolute -top-1.5 -right-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
              >
                NEW
              </span>
            </button>
          </div>
          <CatalogTools
            workType={form.work_type}
            province={form.province}
            lang={lang}
            materials={form.materials}
            onPick={(item) => {
              update({
                materials: [
                  ...form.materials,
                  {
                    id: crypto.randomUUID(),
                    description: item.name,
                    quantity: 1,
                    unit: item.unit,
                    unit_price: item.price,
                    note: '',
                  },
                ],
              });
            }}
            onRefine={(refined) => {
              const map = new Map(refined.map((r) => [r.description, r]));
              const next = form.materials.map((m) => {
                const r = map.get(m.description);
                if (!r) return m;
                return { ...m, unit_price: r.suggested_price, _aiNote: r.note };
              });
              update({ materials: next });
            }}
          />
          {form.materials.length === 0 && (
            <div className="card p-6 text-center text-gray-500 text-sm">{t('mat.empty')}</div>
          )}
          {form.materials.map((m, i) => {
            const total = (Number(m.quantity) || 0) * (Number(m.unit_price) || 0);
            const units = getUnitsFor(form.work_type, lang);
            return (
              <div key={m.id} className="card p-4 space-y-3" data-testid={`material-row-${i}`}>
                <input data-testid={`material-desc-${i}`} className="field" placeholder={t('mat.description')} value={m.description} onChange={(e) => updateMaterial(i, { description: e.target.value })} />
                <div className="grid grid-cols-3 gap-2">
                  <input data-testid={`material-qty-${i}`} className="field" type="number" step="0.01" placeholder={t('mat.qty')} value={m.quantity} onChange={(e) => updateMaterial(i, { quantity: e.target.value })} />
                  <input
                    data-testid={`material-unit-${i}`}
                    list={`units-${i}`}
                    className="field"
                    placeholder={t('mat.unit')}
                    value={m.unit}
                    onChange={(e) => updateMaterial(i, { unit: e.target.value })}
                  />
                  <datalist id={`units-${i}`}>
                    {units.map((u) => <option key={u} value={u} />)}
                  </datalist>
                  <input data-testid={`material-price-${i}`} className="field" type="number" step="0.01" placeholder={t('mat.price')} value={m.unit_price} onChange={(e) => updateMaterial(i, { unit_price: e.target.value })} />
                </div>
                {m._aiNote && (
                  <div className="text-[11px] text-[#7A1F2B]/70 flex items-center gap-1">
                    <Sparkles size={11} /> {m._aiNote}
                  </div>
                )}
                <textarea
                  data-testid={`material-note-${i}`}
                  className="field min-h-[60px] py-2 text-sm"
                  placeholder={lang === 'fr' ? 'Note (couleur, marque, fournisseur…)' : 'Note (color, brand, supplier…)'}
                  value={m.note || ''}
                  onChange={(e) => updateMaterial(i, { note: e.target.value })}
                />
                <div className="flex items-center justify-between">
                  <span className="font-heading font-black text-lg">{total.toFixed(2)} $</span>
                  <button data-testid={`material-remove-${i}`} onClick={() => removeMaterial(i)} className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-red-700 hover:bg-red-50 font-bold text-sm">
                    <Trash2 size={16} /> {t('common.delete')}
                  </button>
                </div>
              </div>
            );
          })}
          <button data-testid="add-material-btn" onClick={addMaterial} className="btn-outline w-full gap-2 border-dashed">
            <Plus size={18} /> {t('mat.add')}
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 mb-1">
            <button
              type="button"
              data-testid="labour-mode-uniform"
              onClick={() => updateLabour({ uniform: true })}
              className={`min-h-[52px] rounded-xl border-2 font-bold text-sm transition-all ${
                form.labour.uniform !== false
                  ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white'
                  : 'border-[#E5DCC9] text-gray-600'
              }`}
            >
              {t('lab.uniform')}
            </button>
            <button
              type="button"
              data-testid="labour-mode-individual"
              onClick={() => {
                const list = (form.labour.individuals && form.labour.individuals.length)
                  ? form.labour.individuals
                  : [{ id: crypto.randomUUID(), name: '', rate: form.labour.hourly_rate || 65, hours: form.labour.hours || 0 }];
                updateLabour({ uniform: false, individuals: list });
              }}
              className={`min-h-[52px] rounded-xl border-2 font-bold text-sm transition-all ${
                form.labour.uniform === false
                  ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white'
                  : 'border-[#E5DCC9] text-gray-600'
              }`}
            >
              {t('lab.individual')}
            </button>
          </div>

          {form.labour.uniform !== false ? (
            <>
              <Field label={t('lab.rate')}>
                <input data-testid="labour-rate" type="number" step="0.01" className="field" value={form.labour.hourly_rate} onChange={(e) => updateLabour({ hourly_rate: e.target.value })} />
              </Field>
              <Field label={t('lab.workers')}>
                <input data-testid="labour-workers" type="number" className="field" value={form.labour.workers} onChange={(e) => updateLabour({ workers: e.target.value })} />
              </Field>
              <Field label={t('lab.hours')}>
                <input data-testid="labour-hours" type="number" step="0.5" className="field" value={form.labour.hours} onChange={(e) => updateLabour({ hours: e.target.value })} />
              </Field>
            </>
          ) : (
            <>
              {(form.labour.individuals || []).map((w, i) => {
                const sub = (Number(w.rate) || 0) * (Number(w.hours) || 0);
                return (
                  <div key={w.id} className="card p-4 space-y-3" data-testid={`worker-row-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-bold uppercase tracking-widest text-[#7A1F2B]/70">
                        {t('lab.worker_n')} {i + 1}
                      </span>
                      <button
                        type="button"
                        data-testid={`worker-remove-${i}`}
                        onClick={() => {
                          const arr = [...form.labour.individuals];
                          arr.splice(i, 1);
                          updateLabour({ individuals: arr });
                        }}
                        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-red-700 hover:bg-red-50 font-bold text-xs"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <input
                      data-testid={`worker-name-${i}`}
                      className="field"
                      placeholder={t('lab.worker_name')}
                      value={w.name || ''}
                      onChange={(e) => {
                        const arr = [...form.labour.individuals];
                        arr[i] = { ...arr[i], name: e.target.value };
                        updateLabour({ individuals: arr });
                      }}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="label !text-[10px]">{t('lab.rate')}</label>
                        <input
                          data-testid={`worker-rate-${i}`}
                          type="number"
                          step="0.01"
                          className="field"
                          value={w.rate}
                          onChange={(e) => {
                            const arr = [...form.labour.individuals];
                            arr[i] = { ...arr[i], rate: e.target.value };
                            updateLabour({ individuals: arr });
                          }}
                        />
                      </div>
                      <div>
                        <label className="label !text-[10px]">{t('lab.hours')}</label>
                        <input
                          data-testid={`worker-hours-${i}`}
                          type="number"
                          step="0.5"
                          className="field"
                          value={w.hours}
                          onChange={(e) => {
                            const arr = [...form.labour.individuals];
                            arr[i] = { ...arr[i], hours: e.target.value };
                            updateLabour({ individuals: arr });
                          }}
                        />
                      </div>
                    </div>
                    <div className="text-right font-heading font-black text-lg">{sub.toFixed(2)} $</div>
                  </div>
                );
              })}
              <button
                type="button"
                data-testid="add-worker-btn"
                onClick={() => {
                  const lastRate = form.labour.individuals?.[form.labour.individuals.length - 1]?.rate || form.labour.hourly_rate || 65;
                  const lastHours = form.labour.individuals?.[form.labour.individuals.length - 1]?.hours || 0;
                  updateLabour({
                    individuals: [
                      ...(form.labour.individuals || []),
                      { id: crypto.randomUUID(), name: '', rate: lastRate, hours: lastHours },
                    ],
                  });
                }}
                className="btn-outline w-full gap-2 border-dashed"
              >
                <Plus size={18} /> {t('lab.add_worker')}
              </button>
            </>
          )}

          <div className="card p-4 bg-[#FAF7F2] flex items-center justify-between">
            <span className="font-bold">{t('lab.total')}</span>
            <span className="font-heading font-black text-2xl">{summary.labourTotal.toFixed(2)} $</span>
          </div>
        </div>
      )}

      {step === 4 && (
        <SummaryStep form={form} summary={summary} update={update} t={t} />
      )}

      <div className="mt-8 space-y-2 pb-24">
        {step < STEPS.length - 1 ? (
          <div className="flex gap-3">
            {step > 0 && (
              <button data-testid="wizard-prev" onClick={prev} className="btn-outline flex-1 gap-2">
                <ChevronLeft size={18} /> {t('wizard.back')}
              </button>
            )}
            <button data-testid="wizard-next" onClick={next} disabled={!canNext()} className="btn-primary flex-[2] gap-2">
              {t('wizard.next')} <ChevronRight size={18} />
            </button>
          </div>
        ) : (
          <>
            <button data-testid="wizard-save" onClick={() => save('save')} className="btn-accent w-full gap-2">
              <Check size={18} /> {t('wizard.save')}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="wizard-save-pdf" onClick={() => save('pdf')} className="btn-primary gap-2">
                <Check size={16} className="hidden sm:inline" />
                PDF
              </button>
              <button data-testid="wizard-print" onClick={() => save('print')} className="btn-primary gap-2">
                <Printer size={16} /> {t('common.print')}
              </button>
            </div>
            <button
              data-testid="wizard-save-as-template"
              onClick={() => {
                setTplName(form.work_type ? `${(WORK_TYPES[lang] && WORK_TYPES[lang][form.work_type]) || form.work_type}` : '');
                setSaveTplOpen(true);
              }}
              className="btn-outline w-full gap-2"
            >
              <BookOpen size={16} /> {lang === 'fr' ? 'Sauvegarder comme modèle' : 'Save as template'}
            </button>
            {step > 0 && (
              <button data-testid="wizard-prev" onClick={prev} className="btn-outline w-full gap-2 !min-h-[48px]">
                <ChevronLeft size={18} /> {t('wizard.back')}
              </button>
            )}
          </>
        )}
      </div>

      <MetreCalculator
        key={calcOpen ? `${calcMaterialIdx ?? 'tile'}-${form.materials.length}` : 'closed'}
        open={calcOpen}
        onClose={() => setCalcOpen(false)}
        lang={lang}
        materials={form.materials}
        prefilledTargetIdx={calcMaterialIdx}
        onApply={({ quantity, unit, targetIdx }) => {
          if (targetIdx === -1) {
            // Just show me — do nothing
            return;
          }
          if (targetIdx === -2 || (targetIdx == null && calcMaterialIdx == null)) {
            // Add as a brand new material row
            update({
              materials: [
                ...form.materials,
                {
                  id: crypto.randomUUID(),
                  description: lang === 'fr' ? 'Matériau' : 'Material',
                  quantity,
                  unit,
                  unit_price: 0,
                  note: '',
                },
              ],
            });
            toast.success(lang === 'fr' ? '✓ Matériau ajouté' : '✓ Material added');
            return;
          }
          const idx = targetIdx != null ? targetIdx : calcMaterialIdx;
          if (idx != null && idx >= 0) updateMaterial(idx, { quantity, unit });
        }}
      />

      {saveTplOpen && (
        <div
          data-testid="save-template-modal"
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setSaveTplOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[#E5DCC9] flex items-center justify-between bg-[#FAF7F2]">
              <div className="font-heading font-black text-lg">
                {lang === 'fr' ? 'Sauvegarder comme modèle' : 'Save as template'}
              </div>
              <button onClick={() => setSaveTplOpen(false)} className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">
                {lang === 'fr'
                  ? 'Sauvegarde la liste des matériaux + main-d\u2019œuvre pour réutiliser cette structure sur tes prochaines soumissions. Le nom et l\u2019adresse du client ne sont pas enregistrés.'
                  : 'Save the material + labour structure to reuse it on your next quotes. Client name and address are NOT saved.'}
              </p>
              <label className="label">{lang === 'fr' ? 'Nom du modèle' : 'Template name'}</label>
              <input
                data-testid="template-name-input"
                className="field"
                placeholder={lang === 'fr' ? 'ex: Rénovation salle de bain' : 'e.g. Bathroom renovation'}
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                autoFocus
              />
              <button
                data-testid="confirm-save-template"
                onClick={async () => {
                  const name = (tplName || '').trim();
                  if (!name) {
                    toast.error(lang === 'fr' ? 'Donne un nom au modèle' : 'Give the template a name');
                    return;
                  }
                  const saved = await saveTemplate({
                    name,
                    work_type: form.work_type,
                    province: form.province,
                    materials: form.materials,
                    labour: form.labour,
                    margin: form.margin,
                    miscFees: form.miscFees,
                    miscNotes: form.miscNotes,
                    notes: form.notes,
                  });
                  if (saved) {
                    toast.success(lang === 'fr' ? '✓ Modèle sauvegardé' : '✓ Template saved');
                    setSaveTplOpen(false);
                    setTplName('');
                  } else {
                    toast.error(lang === 'fr' ? 'Échec sauvegarde' : 'Save failed');
                  }
                }}
                className="btn-primary w-full"
              >
                {lang === 'fr' ? 'Sauvegarder' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {children}
    </div>
  );
}

function SummaryStep({ form, summary, update, t }) {
  const { lang } = useT();
  const isLow = summary.marginPct < 10;
  return (
    <div className="space-y-5">
      <Row label={t('sum.materials')} value={summary.materialsTotal} />
      <Row label={t('sum.labour')} value={summary.labourTotal} />
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label !mb-0">{t('sum.margin')} ({summary.marginPct}%)</label>
          <span className={`font-heading font-black text-lg ${isLow ? 'text-amber-700' : ''}`}>
            {summary.marginAmount.toFixed(2)} $
          </span>
        </div>
        <input
          data-testid="margin-slider"
          type="range"
          min="0"
          max="50"
          step="1"
          value={form.margin}
          onChange={(e) => update({ margin: Number(e.target.value) })}
          className="w-full accent-[#7A1F2B] h-2"
        />
        {isLow && (
          <div className="mt-2 flex items-center gap-2 text-xs font-bold text-amber-700">
            <AlertCircle size={14} /> {lang === 'fr' ? 'Marge sous 10%' : 'Margin under 10%'}
          </div>
        )}
      </div>
      <div>
        <label className="label">{t('sum.misc')}</label>
        <input
          data-testid="misc-fees"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          placeholder="0.00"
          className="field"
          value={form.miscFees === 0 ? '' : form.miscFees}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const v = e.target.value;
            update({ miscFees: v === '' ? 0 : Number(v) });
          }}
        />
        <textarea
          data-testid="misc-notes"
          rows={3}
          maxLength={500}
          placeholder={lang === 'fr'
            ? 'Notes / détails sur les frais divers (ex : déplacement Montréal-Sherbrooke, location grue, permis municipal…)'
            : 'Notes / details about misc fees (e.g., travel Montreal-Sherbrooke, crane rental, city permit…)'}
          className="field mt-2 !min-h-[80px] !py-3 text-sm"
          value={form.miscNotes || ''}
          onChange={(e) => update({ miscNotes: e.target.value })}
        />
        <div className="text-[11px] text-gray-500 mt-1">
          {lang === 'fr'
            ? 'Ces notes seront visibles sur le PDF et dans le courriel envoyé au client.'
            : 'These notes will appear on the PDF and in the email sent to the client.'}
          {' '}
          <span className="text-gray-400">({(form.miscNotes || '').length}/500)</span>
        </div>
      </div>
      <div className="h-px bg-gray-200" />
      <Row label={t('sum.subtotal')} value={summary.subtotal} bold />
      {summary.taxes.map((tx) => <Row key={tx.label} label={tx.label} value={tx.amount} muted />)}
      <div className="card !bg-[#7A1F2B] text-white p-5 flex items-center justify-between">
        <span className="font-heading font-black text-xl">{t('sum.grand')}</span>
        <span data-testid="grand-total" className="font-heading font-black text-3xl tracking-tighter">
          {summary.grandTotal.toFixed(2)} $
        </span>
      </div>
    </div>
  );
}

function Row({ label, value, bold, muted }) {
  return (
    <div className={`flex items-center justify-between ${muted ? 'text-sm text-gray-600' : ''}`}>
      <span className={bold ? 'font-bold' : ''}>{label}</span>
      <span className={`${bold ? 'font-heading font-black text-lg' : 'font-semibold'}`}>
        {(Number(value) || 0).toFixed(2)} $
      </span>
    </div>
  );
}


function CatalogTools({ workType, province, lang, materials, onPick, onRefine }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const items = getCatalogFor(workType, lang);
  const filtered = search.trim()
    ? items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))
    : items;

  const refine = async () => {
    if (!online) {
      toast.error(lang === 'fr' ? 'Pas de réseau internet' : 'No internet connection');
      return;
    }
    if (!materials.length) {
      toast.error(lang === 'fr' ? 'Ajoutez d\'abord des matériaux' : 'Add materials first');
      return;
    }
    setRefreshing(true);
    try {
      // Get the current Supabase JWT so the backend can authenticate the user.
      // Without this header, /api/ai/refine-prices returns 401 — which we used
      // to display as "essai gratuit terminé", confusing users who actually
      // have a valid subscription but a stale local session.
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        toast.error(
          lang === 'fr'
            ? 'Ta session a expiré. Reconnecte-toi pour utiliser les prix par région.'
            : 'Your session expired. Please sign in again to use regional prices.'
        );
        setRefreshing(false);
        return;
      }
      // 🍎 Bridge Apple IAP webhook delay: if the user just bought a sub via
      // StoreKit (sp.iapPurchased.v1 posed by Pricing.jsx), forward the
      // X-IAP-Recent header so the backend grants 30-minute grace access
      // even before the RevenueCat→Supabase webhook fires. Without this the
      // user sees a false "free trial ended" toast right after subscribing.
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const ts = window.localStorage.getItem('sp.iapPurchased.v1');
        if (ts) {
          const age = Date.now() - Number(ts);
          if (age >= 0 && age <= 30 * 60 * 1000) {
            headers['X-IAP-Recent'] = String(ts);
          }
        }
      } catch { /* localStorage disabled */ }
      const { data } = await axios.post(`${API}/ai/refine-prices`, {
        province,
        work_type: workType,
        lang,
        materials: materials.map((m) => ({
          description: m.description || '',
          unit: m.unit || '',
          current_price: Number(m.unit_price) || 0,
        })),
      }, {
        timeout: 60000,
        headers,
      });
      onRefine(data.materials || []);
      toast.success(
        lang === 'fr'
          ? `✓ Prix mis à jour pour ${province}`
          : `✓ Prices updated for ${province}`
      );
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        toast.error(
          lang === 'fr'
            ? 'Ta session a expiré. Reconnecte-toi pour utiliser les prix par région.'
            : 'Your session expired. Please sign in again.'
        );
      } else if (code === 402) {
        toast.error(
          lang === 'fr'
            ? 'Ton essai gratuit est terminé. Souscris pour utiliser les prix par région.'
            : 'Your free trial has ended. Subscribe to use AI-powered regional prices.'
        );
      } else if (code === 500 || code === 502) {
        toast.error(
          lang === 'fr'
            ? 'Le service IA est temporairement indisponible. Réessaye dans quelques secondes.'
            : 'AI service temporarily unavailable. Try again in a few seconds.'
        );
      } else {
        toast.error(
          lang === 'fr' ? 'Erreur lors de la mise à jour des prix' : 'Failed to refresh prices'
        );
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          data-testid="open-catalog-btn"
          onClick={() => setOpen(true)}
          className="btn-outline gap-2 !min-h-[48px] text-sm"
        >
          <BookOpen size={16} /> {lang === 'fr' ? 'Catalogue' : 'Catalog'}
        </button>
        <button
          data-testid="refine-prices-btn"
          onClick={refine}
          disabled={refreshing || !online}
          className="btn-outline gap-2 !min-h-[48px] text-sm disabled:opacity-50"
        >
          <Sparkles size={16} className={refreshing ? 'animate-pulse' : ''} />
          {refreshing
            ? (lang === 'fr' ? 'Analyse…' : 'Analyzing…')
            : (lang === 'fr' ? 'Prix par région' : 'Prices by region')}
        </button>
      </div>

      {open && (
        <div
          data-testid="catalog-modal"
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[#E5DCC9] flex items-center justify-between gap-2 sticky top-0 bg-white">
              <div>
                <div className="font-heading font-black text-xl">
                  {lang === 'fr' ? 'Catalogue' : 'Catalog'}
                </div>
                <div className="text-xs text-gray-500">{workType || (lang === 'fr' ? 'Choisissez un type de travaux' : 'Choose a work type')}</div>
              </div>
              <button
                data-testid="close-catalog"
                onClick={() => setOpen(false)}
                className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-gray-100"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-3 border-b border-[#E5DCC9]">
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={lang === 'fr' ? 'Rechercher un matériau…' : 'Search material…'}
                className="field !min-h-[44px]"
              />
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-[#F0E8D9]">
              <button
                data-testid="catalog-add-custom"
                onClick={() => {
                  onPick({
                    name: search.trim() || (lang === 'fr' ? 'Matériau personnalisé' : 'Custom material'),
                    unit: 'un',
                    price: 0,
                  });
                  setOpen(false);
                  setSearch('');
                }}
                className="w-full text-left p-4 bg-[#FAF7F2] hover:bg-[#F0E8D9] transition-colors flex items-center gap-3 border-b-2 border-dashed border-[#E5DCC9]"
              >
                <div className="w-10 h-10 rounded-xl bg-[#7A1F2B] text-white flex items-center justify-center shrink-0">
                  <Plus size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm">
                    {search.trim()
                      ? (lang === 'fr' ? `Ajouter « ${search.trim()} »` : `Add "${search.trim()}"`)
                      : (lang === 'fr' ? 'Créer un matériau personnalisé' : 'Add custom material')}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {lang === 'fr'
                      ? 'Ajoutez votre propre item si non listé'
                      : 'Add your own item if not listed'}
                  </div>
                </div>
              </button>
              {filtered.length === 0 && search.trim() && (
                <div className="p-6 text-center text-gray-500 text-xs">
                  {lang === 'fr' ? 'Aucun résultat dans le catalogue.' : 'No results in catalog.'}
                </div>
              )}
              {filtered.map((it) => (
                <button
                  key={it.name}
                  data-testid={`catalog-item-${it.name}`}
                  onClick={() => {
                    onPick(it);
                    toast.success('+ ' + it.name, { duration: 1500 });
                    setOpen(false);
                    setSearch('');
                  }}
                  className="w-full text-left p-4 hover:bg-[#FAF7F2] transition-colors flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{it.name}</div>
                    <div className="text-xs text-gray-500">{it.unit}</div>
                  </div>
                  <div className="font-heading font-black text-[#7A1F2B] shrink-0">
                    {it.price.toFixed(2)} $
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
